import { useState, useEffect } from 'react';
import './CookieConsent.css';

// Declaração de tipos para gtag e fbq
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}

interface ConsentSettings {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
}

const CONSENT_STORAGE_KEY = 'sophya_cookie_consent';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentSettings>({
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });

  useEffect(() => {
    // Verificar se usuário já deu consentimento
    const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    
    if (savedConsent) {
      const parsed = JSON.parse(savedConsent);
      setConsent(parsed);
      applyConsent(parsed);
    } else {
      // Mostrar banner se não há consentimento salvo
      setShowBanner(true);
      // Definir consentimento padrão como negado
      applyDefaultConsent();
    }
  }, []);

  const applyDefaultConsent = () => {
    // Configurar gtag com consentimento padrão negado
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        functionality_storage: 'granted',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      });
    }
  };

  const applyConsent = (settings: ConsentSettings) => {
    // Atualizar consentimento no Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', settings);
    }

    // Controlar Facebook Pixel baseado no consentimento de ads
    if (settings.ad_storage === 'granted' && settings.ad_user_data === 'granted') {
      // Ativar tracking completo do Facebook
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'grant');
      }
    } else {
      // Revogar consentimento do Facebook
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'revoke');
      }
    }
  };

  const handleAcceptAll = () => {
    const allGranted: ConsentSettings = {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
      security_storage: 'granted',
    };
    
    setConsent(allGranted);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(allGranted));
    applyConsent(allGranted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const allDenied: ConsentSettings = {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted', // Mantém funcionalidade básica
      personalization_storage: 'denied',
      security_storage: 'granted', // Mantém segurança
    };
    
    setConsent(allDenied);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(allDenied));
    applyConsent(allDenied);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    applyConsent(consent);
    setShowBanner(false);
    setShowDetails(false);
  };

  const toggleConsent = (key: keyof ConsentSettings) => {
    if (key === 'functionality_storage' || key === 'security_storage') {
      return; // Não permitir desativar cookies essenciais
    }
    
    setConsent(prev => ({
      ...prev,
      [key]: prev[key] === 'granted' ? 'denied' : 'granted',
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <div className="cookie-consent-header">
            <i className="fas fa-cookie-bite cookie-icon"></i>
            <h4>Utilizamos Cookies</h4>
          </div>
          
          <p className="cookie-consent-text">
            Usamos cookies para melhorar sua experiência, analisar o tráfego do site e personalizar conteúdo e anúncios. 
            Ao clicar em "Aceitar Todos", você concorda com o uso de cookies conforme nossa{' '}
            <a href="/docs/politica-privacidade.html" target="_blank" rel="noopener noreferrer">
              Política de Privacidade
            </a>.
          </p>

          {showDetails && (
            <div className="cookie-consent-details">
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <strong>Cookies Essenciais</strong>
                    <small>Necessários para o funcionamento do site</small>
                  </div>
                  <span className="cookie-toggle active disabled">
                    <i className="fas fa-check"></i> Sempre ativo
                  </span>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <strong>Cookies de Analytics</strong>
                    <small>Nos ajudam a entender como você usa o site (Google Analytics)</small>
                  </div>
                  <button 
                    className={`cookie-toggle ${consent.analytics_storage === 'granted' ? 'active' : ''}`}
                    onClick={() => toggleConsent('analytics_storage')}
                  >
                    {consent.analytics_storage === 'granted' ? (
                      <><i className="fas fa-check"></i> Ativo</>
                    ) : (
                      <><i className="fas fa-times"></i> Inativo</>
                    )}
                  </button>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <strong>Cookies de Publicidade</strong>
                    <small>Para medir anúncios e remarketing (Google Ads, Meta Pixel)</small>
                  </div>
                  <button 
                    className={`cookie-toggle ${consent.ad_storage === 'granted' ? 'active' : ''}`}
                    onClick={() => {
                      const newValue = consent.ad_storage === 'granted' ? 'denied' : 'granted';
                      setConsent(prev => ({
                        ...prev,
                        ad_storage: newValue,
                        ad_user_data: newValue,
                        ad_personalization: newValue,
                      }));
                    }}
                  >
                    {consent.ad_storage === 'granted' ? (
                      <><i className="fas fa-check"></i> Ativo</>
                    ) : (
                      <><i className="fas fa-times"></i> Inativo</>
                    )}
                  </button>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <strong>Cookies de Personalização</strong>
                    <small>Para lembrar suas preferências e personalizar conteúdo</small>
                  </div>
                  <button 
                    className={`cookie-toggle ${consent.personalization_storage === 'granted' ? 'active' : ''}`}
                    onClick={() => toggleConsent('personalization_storage')}
                  >
                    {consent.personalization_storage === 'granted' ? (
                      <><i className="fas fa-check"></i> Ativo</>
                    ) : (
                      <><i className="fas fa-times"></i> Inativo</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="cookie-consent-actions">
            <button 
              className="btn-cookie-details"
              onClick={() => setShowDetails(!showDetails)}
            >
              <i className={`fas fa-${showDetails ? 'chevron-up' : 'cog'} me-2`}></i>
              {showDetails ? 'Ocultar Detalhes' : 'Gerenciar Preferências'}
            </button>
            
            <div className="cookie-consent-buttons">
              <button 
                className="btn-cookie-reject"
                onClick={handleRejectAll}
              >
                Rejeitar
              </button>
              
              {showDetails ? (
                <button 
                  className="btn-cookie-save"
                  onClick={handleSavePreferences}
                >
                  Salvar Preferências
                </button>
              ) : (
                <button 
                  className="btn-cookie-accept"
                  onClick={handleAcceptAll}
                >
                  Aceitar Todos
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
